import { DateTime } from 'luxon'

import type {
  LocalPqResourceSet,
  LocalPqRun,
  PqMarketCatalogueEntry,
  ResourceSetMember,
} from '@/features/prequalification/localControl/types'
import type { PrequalificationResourceSet } from '@/features/prequalification/types'

/**
 * Static fixtures for tests and Storybook-style rendering. The mock endpoints
 * (`localPqMock`) build a live in-memory store seeded from these, so they stay
 * stable for assertions while the store mutates independently.
 */

/** The catalogue `GET /markets` serves. */
export const MOCK_LOCAL_PQ_MARKETS: PqMarketCatalogueEntry[] = [
  {
    market: 'FCR_N',
    label: 'FCR-N',
    groups: [
      {
        label: 'FCR-N',
        tests: [
          { testName: 'RAMP_N_MAX', label: 'Ramp N Max' },
          { testName: 'RAMP_N_LOW', label: 'Ramp N Low' },
          { testName: 'SINE_10_N', label: 'Sine 10s N' },
          { testName: 'SINE_15_N', label: 'Sine 15s N' },
          { testName: 'SINE_25_N', label: 'Sine 25s N' },
          { testName: 'SINE_40_N', label: 'Sine 40s N' },
          { testName: 'SINE_50_N', label: 'Sine 50s N' },
          { testName: 'SINE_60_N', label: 'Sine 60s N' },
          { testName: 'SINE_70_N', label: 'Sine 70s N' },
          { testName: 'SINE_90_N', label: 'Sine 90s N' },
          { testName: 'SINE_150_N', label: 'Sine 150s N' },
          { testName: 'SINE_300_N', label: 'Sine 300s N' },
          { testName: 'LER_N', label: 'LER N' },
          { testName: 'LIVE_N', label: 'Live N' },
        ],
      },
    ],
  },
  {
    market: 'FCR_D',
    label: 'FCR-D',
    groups: [
      {
        label: 'FCR-D Up',
        tests: [
          { testName: 'RAMP_UP_MAX', label: 'Ramp Up Max' },
          { testName: 'RAMP_UP_LOW', label: 'Ramp Up Low' },
          { testName: 'SINE_10_UP', label: 'Sine 10s Up' },
          { testName: 'SINE_15_UP', label: 'Sine 15s Up' },
          { testName: 'SINE_25_UP', label: 'Sine 25s Up' },
          { testName: 'SINE_40_UP', label: 'Sine 40s Up' },
          { testName: 'SINE_50_UP', label: 'Sine 50s Up' },
          { testName: 'SINE_60_UP', label: 'Sine 60s Up' },
          { testName: 'SINE_70_UP', label: 'Sine 70s Up' },
          { testName: 'LER_D_UP', label: 'LER D Up' },
          { testName: 'LIVE_UP', label: 'Live Up' },
        ],
      },
      {
        label: 'FCR-D Down',
        tests: [
          { testName: 'RAMP_DOWN_MAX', label: 'Ramp Down Max' },
          { testName: 'RAMP_DOWN_LOW', label: 'Ramp Down Low' },
          { testName: 'SINE_10_DOWN', label: 'Sine 10s Down' },
          { testName: 'SINE_15_DOWN', label: 'Sine 15s Down' },
          { testName: 'SINE_25_DOWN', label: 'Sine 25s Down' },
          { testName: 'SINE_40_DOWN', label: 'Sine 40s Down' },
          { testName: 'SINE_50_DOWN', label: 'Sine 50s Down' },
          { testName: 'SINE_60_DOWN', label: 'Sine 60s Down' },
          { testName: 'SINE_70_DOWN', label: 'Sine 70s Down' },
          { testName: 'LER_D_DOWN', label: 'LER D Down' },
          { testName: 'LIVE_DOWN', label: 'Live Down' },
        ],
      },
      {
        label: 'FCR combined',
        tests: [
          { testName: 'RAMP_UP_AND_N', label: 'Ramp Up And N' },
          { testName: 'RAMP_DOWN_AND_N', label: 'Ramp Down And N' },
        ],
      },
    ],
  },
  {
    market: 'MFRR',
    label: 'mFRR',
    groups: [
      {
        label: 'mFRR Up',
        tests: [
          { testName: 'MFRR_UP_MAX', label: 'mFRR Up Max' },
          { testName: 'MFRR_UP_LOW', label: 'mFRR Up Low' },
        ],
      },
      {
        label: 'mFRR Down',
        tests: [
          { testName: 'MFRR_DOWN_MAX', label: 'mFRR Down Max' },
          { testName: 'MFRR_DOWN_LOW', label: 'mFRR Down Low' },
        ],
      },
    ],
  },
]

export function mockMarket(market: string): PqMarketCatalogueEntry {
  const entry = MOCK_LOCAL_PQ_MARKETS.find((candidate) => candidate.market === market)
  if (!entry) throw new Error(`No mock market ${market}`)
  return entry
}

/** Battery units that seed the primary demo resource set (and its runs). */
export const MOCK_BATTERY_RESOURCES: ResourceSetMember[] = [
  { resourceId: '4f9c2c2e-1a11-4d6a-9b1a-2e7c5d9a1001', name: 'Battery Unit A — 1 MW' },
  { resourceId: '4f9c2c2e-1a11-4d6a-9b1a-2e7c5d9a1002', name: 'Battery Unit B — 1 MW' },
  { resourceId: '4f9c2c2e-1a11-4d6a-9b1a-2e7c5d9a1003', name: 'Battery Unit C — 2 MW' },
]

export const MOCK_BESS_RESOURCES: ResourceSetMember[] = [
  { resourceId: '7a1b3c4d-2e22-4f7b-8c2b-3f8d6eab2001', name: 'BESS North — 1.5 MW' },
  { resourceId: '7a1b3c4d-2e22-4f7b-8c2b-3f8d6eab2002', name: 'BESS South — 1.5 MW' },
]

export const MOCK_RESERVE_RESOURCES: ResourceSetMember[] = [
  { resourceId: '9c2d4e5f-3a33-4a8c-9d3c-4a9e7fbc3001', name: 'Reserve Unit 1 — 2 MW' },
  { resourceId: '9c2d4e5f-3a33-4a8c-9d3c-4a9e7fbc3002', name: 'Reserve Unit 2 — 2 MW' },
  { resourceId: '9c2d4e5f-3a33-4a8c-9d3c-4a9e7fbc3003', name: 'Reserve Unit 3 — 1 MW' },
  { resourceId: '9c2d4e5f-3a33-4a8c-9d3c-4a9e7fbc3004', name: 'Reserve Unit 4 — 1 MW' },
]

/** A flat pool the resource search filters over (the seeded sets + a few extra units). */
export const MOCK_SEARCHABLE_RESOURCES: ResourceSetMember[] = [
  ...MOCK_BATTERY_RESOURCES,
  ...MOCK_BESS_RESOURCES,
  ...MOCK_RESERVE_RESOURCES,
  { resourceId: 'c3e5f7a9-4b44-4b9d-ae4d-5baf80cd4001', name: 'NO2 · Statkraft · Hydro Reserve 7' },
  { resourceId: 'c3e5f7a9-4b44-4b9d-ae4d-5baf80cd4002', name: 'DK1 · Ørsted · Flex Pool Alpha' },
  { resourceId: 'c3e5f7a9-4b44-4b9d-ae4d-5baf80cd4003', name: 'DK1 · Ørsted · Flex Pool Beta' },
]

export const MOCK_RESOURCE_SET_ID = 'b1a2c3d4-0001-4a5b-8c6d-1e2f3a4b5c6d'

const PRIMARY_RESOURCES = MOCK_BATTERY_RESOURCES

export const MOCK_LOCAL_PQ_RESOURCE_SET: LocalPqResourceSet = {
  id: MOCK_RESOURCE_SET_ID,
  scope: 'local-control',
  name: 'Vattenfall SE3 — battery PQ',
  resources: PRIMARY_RESOURCES.map((resource) => ({ resourceId: resource.resourceId, name: resource.name })),
  requestedAt: DateTime.fromISO('2026-06-02T08:00:00.000Z'),
  startAt: null,
  updatedAt: DateTime.fromISO('2026-06-02T09:15:00.000Z'),
}

const REQUESTED = DateTime.fromISO('2026-06-02T08:00:00.000Z')
const STARTED = DateTime.fromISO('2026-06-02T09:00:00.000Z')
const SETTLED = DateTime.fromISO('2026-06-02T09:15:00.000Z')

/**
 * Multi-test runs for the primary resourceSet: one fully-passed FCR-N test, one
 * in-flight FCR-N test, and a partially-failed FCR-D test — enough variety to
 * exercise the per-test / per-market aggregation on the detail page.
 */
export const MOCK_LOCAL_PQ_RUNS: LocalPqRun[] = [
  // FCR-N · Ramp N Max — all passed
  ...PRIMARY_RESOURCES.map(
    (resource, index): LocalPqRun => ({
      id: `a0000001-0000-4000-8000-00000000100${index + 1}`,
      resourceSetId: MOCK_RESOURCE_SET_ID,
      resourceId: resource.resourceId,
      resourceName: resource.name,
      testName: 'RAMP_N_MAX',
      requestedAt: REQUESTED,
      startAt: STARTED,
      phase: 'PASSED',
      lastStatusAt: SETTLED,
      updatedAt: SETTLED,
    }),
  ),
  // FCR-N · Sine 25s N — in flight
  {
    id: 'a0000001-0000-4000-8000-000000002001',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[0].resourceId,
    resourceName: PRIMARY_RESOURCES[0].name,
    testName: 'SINE_25_N',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'PASSED',
    lastStatusAt: SETTLED,
    updatedAt: SETTLED,
  },
  {
    id: 'a0000001-0000-4000-8000-000000002002',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[1].resourceId,
    resourceName: PRIMARY_RESOURCES[1].name,
    testName: 'SINE_25_N',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'STARTED',
    lastStatusAt: SETTLED,
    updatedAt: SETTLED,
  },
  {
    id: 'a0000001-0000-4000-8000-000000002003',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[2].resourceId,
    resourceName: PRIMARY_RESOURCES[2].name,
    testName: 'SINE_25_N',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'ENQUEUED',
    lastStatusAt: null,
    updatedAt: REQUESTED,
  },
  // FCR-D · Ramp Up Max — one failure
  {
    id: 'a0000001-0000-4000-8000-000000003001',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[0].resourceId,
    resourceName: PRIMARY_RESOURCES[0].name,
    testName: 'RAMP_UP_MAX',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'FAILED',
    lastStatusAt: SETTLED,
    updatedAt: SETTLED,
  },
  {
    id: 'a0000001-0000-4000-8000-000000003002',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[1].resourceId,
    resourceName: PRIMARY_RESOURCES[1].name,
    testName: 'RAMP_UP_MAX',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'PASSED',
    lastStatusAt: SETTLED,
    updatedAt: SETTLED,
  },
  {
    id: 'a0000001-0000-4000-8000-000000003003',
    resourceSetId: MOCK_RESOURCE_SET_ID,
    resourceId: PRIMARY_RESOURCES[2].resourceId,
    resourceName: PRIMARY_RESOURCES[2].name,
    testName: 'RAMP_UP_MAX',
    requestedAt: REQUESTED,
    startAt: STARTED,
    phase: 'PASSED',
    lastStatusAt: SETTLED,
    updatedAt: SETTLED,
  },
]

export const MOCK_PREQUALIFICATION_RESOURCE_SETS: PrequalificationResourceSet[] = [
  {
    id: MOCK_RESOURCE_SET_ID,
    scope: 'local-control',
    name: 'Vattenfall SE3 — battery PQ',
    resourceCount: 3,
    markets: [
      { market: 'FCR_N', phase: 'STARTED' },
      { market: 'FCR_D', phase: 'FAILED' },
    ],
    phase: 'STARTED',
    requestedAt: DateTime.fromISO('2026-06-02T08:00:00.000Z'),
    startAt: null,
    lastStatusAt: DateTime.fromISO('2026-06-02T09:15:00.000Z'),
  },
  {
    id: 'b1a2c3d4-0002-4a5b-8c6d-1e2f3a4b5c6d',
    scope: 'local-control',
    name: 'Fortum SE4 — BESS qualification',
    resourceCount: 2,
    markets: [{ market: 'FCR_N', phase: 'SCHEDULED' }],
    phase: 'SCHEDULED',
    requestedAt: DateTime.fromISO('2026-06-01T12:00:00.000Z'),
    startAt: DateTime.fromISO('2026-06-05T06:00:00.000Z'),
    lastStatusAt: DateTime.fromISO('2026-06-01T12:00:00.000Z'),
  },
  {
    id: 'b1a2c3d4-0003-4a5b-8c6d-1e2f3a4b5c6d',
    scope: 'local-control',
    name: 'Helen FI — reserve units',
    resourceCount: 4,
    markets: [{ market: 'FCR_D', phase: 'PASSED' }],
    phase: 'PASSED',
    requestedAt: DateTime.fromISO('2026-05-30T07:00:00.000Z'),
    startAt: DateTime.fromISO('2026-05-30T08:00:00.000Z'),
    lastStatusAt: DateTime.fromISO('2026-05-30T08:42:00.000Z'),
  },
  {
    id: 'b1a2c3d4-0004-4a5b-8c6d-1e2f3a4b5c6d',
    scope: 'local-control',
    name: 'Ad-hoc unit — 0.5 MW trial',
    resourceCount: 1,
    markets: [{ market: 'FCR_D', phase: 'FAILED' }],
    phase: 'FAILED',
    requestedAt: DateTime.fromISO('2026-05-29T10:00:00.000Z'),
    startAt: DateTime.fromISO('2026-05-29T10:00:00.000Z'),
    lastStatusAt: DateTime.fromISO('2026-05-29T10:18:00.000Z'),
  },
]

export const MOCK_PREQUALIFICATION_RESOURCE_SET: PrequalificationResourceSet = MOCK_PREQUALIFICATION_RESOURCE_SETS[0]
